import { CheckoutError } from "@circle-react/components/Paywalls/Checkout/CheckoutError";
import { useCheckoutSession } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionContext";
import { checkoutOwnershipOptionsForTarget } from "@circle-react/components/Paywalls/Checkout/sessionOwnership";
import { ApiError } from "@circle-react/config/CustomErrors";
import { paywallCheckout } from "@circle-react/helpers/urlHelpers/internalApi/paywalls/checkout";
import type { UpgradePreview } from "@circle-react/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { v4 as uuidv4 } from "uuid";

import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";

import { isCurrentUpgradePreviewRequest } from "../helpers/upgradePreviewRequestHelpers";
import type { UpgradePreviewRequestIdentity } from "../helpers/upgradePreviewRequestHelpers";
import { useUpgradePricePreviews } from "./useUpgradePricePreviews";

type CouponMode = UpgradePreview["coupon_mode"];

interface UseUpgradePreviewProps {
  checkoutSessionId: number;
  communityId: number;
  disabled?: boolean;
  paywallId: number;
  stepId: number;
  visiblePriceIds: number[];
}

type RequestParams = UpgradePreviewRequestIdentity;

const selectedUpgradePrices = new Map<string, number>();

export const isUpgradeCouponError = (error: unknown) => {
  if (!(error instanceof CheckoutError)) return false;

  if (error.isLegacyPaywallError()) {
    const details = error.getExtra().legacy_paywall_error_details;
    return isCouponFailureDetails(details);
  }

  if (error.isNewPaywallError()) {
    const details = error.getExtra();
    return (
      details.paywall_failure_category === "paywall" &&
      typeof details.paywall_failure_code === "string" &&
      details.paywall_failure_code.startsWith("coupon_")
    );
  }

  return false;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object";

const isCouponFailureDetails = (details: unknown) => {
  if (!isRecord(details)) return false;

  return (
    details.category === "paywall" &&
    typeof details.code === "string" &&
    details.code.startsWith("coupon_")
  );
};

const hasSamePriceIds = (first: number[], second: number[]) => {
  const firstIds = new Set(first);
  const secondIds = new Set(second);

  return (
    firstIds.size === first.length &&
    secondIds.size === second.length &&
    firstIds.size === secondIds.size &&
    [...firstIds].every(id => secondIds.has(id))
  );
};

export const useUpgradePreview = ({
  communityId,
  checkoutSessionId,
  paywallId,
  stepId,
  visiblePriceIds,
  disabled = false,
}: UseUpgradePreviewProps) => {
  const { ownershipToken, session } = useCheckoutSession();
  const matchingOwnershipToken =
    session?.id === checkoutSessionId ? ownershipToken : null;
  const termsKey = `${communityId}:${checkoutSessionId}:${paywallId}:${stepId}`;
  const [selectedPaywallPriceId, setSelectedPaywallPriceId] = useState<
    number | undefined
  >(() => {
    const savedId = selectedUpgradePrices.get(termsKey);
    return savedId && visiblePriceIds.includes(savedId) ? savedId : undefined;
  });
  const [couponMode, setCouponMode] = useState<CouponMode>("policy");
  const [preview, setPreview] = useState<UpgradePreview>();
  const [previewError, setPreviewError] = useState<unknown>();
  const [couponFailure, setCouponFailure] = useState<unknown>();
  const requestIdentityRef = useRef<UpgradePreviewRequestIdentity | undefined>(
    undefined,
  );
  const hasRequestedOnMount = useRef(false);

  const { isPending, mutate } = useMutation({
    mutationFn: ({
      couponMode,
      requestId,
      selectedPaywallPriceId,
    }: RequestParams) =>
      reactQueryGet<UpgradePreview>(
        paywallCheckout.sessionStep.upgradePreview({
          couponMode,
          paywallId,
          requestId,
          selectedPaywallPriceId,
          stepId,
        }),
        checkoutOwnershipOptionsForTarget(
          ownershipToken,
          session?.id,
          checkoutSessionId,
        ),
      ),
    onSuccess: (
      response,
      { couponMode: mode, requestId, selectedPaywallPriceId: priceId },
    ) => {
      const responseIdentity = {
        couponMode: mode,
        requestId,
        selectedPaywallPriceId: priceId,
      };
      const priceOptions = Array.isArray(response.price_options)
        ? response.price_options
        : [];
      const selectedPriceOption = priceOptions.find(
        option =>
          option.paywall_price_id === response.selected_paywall_price_id,
      );
      if (
        !isCurrentUpgradePreviewRequest(
          responseIdentity,
          requestIdentityRef.current,
        )
      ) {
        return;
      }
      if (
        response.request_id !== requestId ||
        response.coupon_mode !== mode ||
        (priceId !== undefined &&
          response.selected_paywall_price_id !== priceId) ||
        (priceId === undefined &&
          response.selected_paywall_price_id !==
            response.default_paywall_price_id) ||
        response.session_step_id !== stepId ||
        response.destination.paywall_price_id !==
          response.selected_paywall_price_id ||
        !response.eligible_paywall_price_ids.includes(
          response.default_paywall_price_id,
        ) ||
        !hasSamePriceIds(
          response.eligible_paywall_price_ids,
          visiblePriceIds,
        ) ||
        !response.eligible_paywall_price_ids.includes(
          response.selected_paywall_price_id,
        ) ||
        !hasSamePriceIds(
          priceOptions.map(option => option.paywall_price_id),
          response.eligible_paywall_price_ids,
        ) ||
        !selectedPriceOption ||
        selectedPriceOption.list_amount.amount !==
          response.destination.list_amount.amount
      ) {
        setPreview(undefined);
        setPreviewError(CheckoutError.generic("useUpgradePreview"));
        return;
      }

      setSelectedPaywallPriceId(response.selected_paywall_price_id);
      selectedUpgradePrices.set(termsKey, response.selected_paywall_price_id);
      setPreview(response);
      if (mode === "policy") setCouponFailure(undefined);
    },
    onError: (error, params) => {
      if (!isCurrentUpgradePreviewRequest(params, requestIdentityRef.current)) {
        return;
      }
      const checkoutError =
        error instanceof ApiError
          ? CheckoutError.fromApiError("useUpgradePreview", error)
          : CheckoutError.fromUnknownError("useUpgradePreview", error);
      setPreview(undefined);
      setPreviewError(checkoutError);
      if (
        params.couponMode === "policy" &&
        isUpgradeCouponError(checkoutError)
      ) {
        setCouponFailure(checkoutError);
      }
    },
  });

  const requestPreview = useCallback(
    (
      priceId: number | undefined,
      mode: CouponMode,
      {
        keepPreview = false,
        allowAfterAcceptance = false,
      }: { keepPreview?: boolean; allowAfterAcceptance?: boolean } = {},
    ) => {
      if (disabled && !allowAfterAcceptance) return;

      const requestId = uuidv4();
      const requestIdentity = {
        couponMode: mode,
        requestId,
        selectedPaywallPriceId: priceId,
      };
      requestIdentityRef.current = requestIdentity;
      setSelectedPaywallPriceId(priceId);
      setCouponMode(mode);
      if (!keepPreview || mode !== couponMode) setPreview(undefined);
      setPreviewError(undefined);
      if (mode === "policy") setCouponFailure(undefined);

      mutate({
        couponMode: mode,
        requestId,
        selectedPaywallPriceId: priceId,
      });
    },
    [couponMode, disabled, mutate],
  );

  useEffect(() => {
    if (hasRequestedOnMount.current) return;
    hasRequestedOnMount.current = true;
    if (disabled) return;
    requestPreview(selectedPaywallPriceId, couponMode);
  }, [couponMode, disabled, requestPreview, selectedPaywallPriceId]);

  const selectPrice = (priceId: number) => {
    if (!visiblePriceIds.includes(priceId)) return;
    requestPreview(priceId, "policy", {
      keepPreview: couponMode === "policy",
    });
  };

  const retry = () => requestPreview(selectedPaywallPriceId, couponMode);

  const continueWithoutCoupon = () => {
    if (!selectedPaywallPriceId || !isUpgradeCouponError(previewError)) return;
    requestPreview(selectedPaywallPriceId, "without_coupon");
  };

  const refreshWithoutCoupon = () => {
    if (!selectedPaywallPriceId) return;
    requestPreview(selectedPaywallPriceId, "without_coupon", {
      allowAfterAcceptance: true,
    });
  };

  const { isLoadingPricePreviews, pricePreviews } = useUpgradePricePreviews({
    communityId,
    checkoutSessionId,
    ownershipToken: matchingOwnershipToken,
    paywallId,
    stepId,
    visiblePriceIds,
    couponMode,
    preview,
    disabled,
  });
  return {
    continueWithoutCoupon,
    refreshWithoutCoupon,
    couponFailure,
    couponMode,
    isLoading: isPending || isLoadingPricePreviews,
    isRefreshing: isPending && preview !== undefined,
    preview: isLoadingPricePreviews ? undefined : preview,
    pricePreviews,
    previewError,
    retry,
    selectPrice,
    selectedPaywallPriceId,
  };
};
